import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { contact } from "@/lib/healic-data";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { notifyForm } from "@/lib/notifyForm";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Healic — Book Appointment, Call or WhatsApp" },
      { name: "description", content: "Reach Healic 7 Am to 12 Am — call +91 95887 95887, email info@healic.in, or chat with us on WhatsApp to book an appointment." },
      { property: "og:title", content: "Contact Healic" },
      { property: "og:description", content: "7 Am to 12 Am care helpline. Quick, secure appointment booking." },
      { property: "og:url", content: "https://healic.in/contact" },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") ?? "").trim();
    const phone = String(fd.get("phone") ?? "").trim();
    if (name.length < 2) return toast.error("Please enter your name");
    if (!/^[0-9+\-\s()]{7,20}$/.test(phone)) return toast.error("Please enter a valid phone");
    setSubmitting(true);
    const ok = await notifyForm({
      formType: "Contact / Appointment Request",
      source: "Contact page",
      fields: [
        { label: "Name", value: name },
        { label: "Phone", value: phone },
        { label: "Email", value: String(fd.get("email") ?? "") },
        { label: "Preferred Date", value: String(fd.get("date") ?? "") },
        { label: "Service / Specialty", value: String(fd.get("service") ?? "") },
        { label: "Message", value: String(fd.get("message") ?? "") },
      ],
    });
    setSubmitting(false);
    if (ok) {
      toast.success("Thanks! Our team will reach out shortly.");
      form.reset();
    } else {
      toast.error("Could not submit. Please call +91 95887 95887.");
    }
  };
  return (
    <PageShell>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Contact</p>
          <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">Book an appointment</h1>
          <p className="mt-4 max-w-2xl opacity-85">Quick, secure booking — no redirects. Or reach our care team 7 Am to 12 Am.</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 lg:grid-cols-[1.4fr_1fr] lg:px-8">
        <form
          className="rounded-3xl border border-border bg-card p-8 shadow-soft"
          onSubmit={onSubmit}
        >
          <h2 className="font-display text-xl font-semibold">Tell us about your Requirment{"\u00a0"}</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field label="Full name" name="name" required />
            <Field label="Mobile number" name="phone" type="tel" required />
            <Field label="Email (optional)" name="email" type="email" />
            <Field label="Preferred date" name="date" type="date" />
            <div className="sm:col-span-2">
              <label htmlFor="contact-service" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Service / specialty
              </label>
              <select id="contact-service" className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm" name="service">
                <option>General Surgery</option>
                <option>Gynaecology & Obstetrics</option>
                <option>Orthopaedics</option>
                <option>Urology</option>
                <option>ENT</option>
                <option>Ophthalmology</option>
                <option>OPD consultation</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="contact-message" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Message</label>
              <textarea id="contact-message" rows={4} className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm" name="message" />
            </div>

          </div>
          <label className="mt-5 flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" defaultChecked className="mt-0.5" />
            I agree to be contacted on phone &amp; WhatsApp for my appointment.
          </label>
          <button disabled={submitting} className="mt-6 w-full rounded-pill bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground shadow-cta hover:opacity-90 disabled:opacity-60">
            {submitting ? "Submitting..." : "Request appointment"}
          </button>
        </form>

        <aside className="space-y-4">
          <ContactCard
            icon={Phone}
            title="7 Am to 12 Am helpline"
            body={contact.phone}
            sub="Speak to our care team anytime"
            href={`tel:${contact.phoneTel}`}
            cta="Call now"
          />
          <ContactCard
            icon={Mail}
            title="Email support"
            body={contact.email}
            sub="Share reports or documents easily"
            href={`mailto:${contact.email}`}
            cta="Email now"
          />
          <ContactCard
            icon={MessageCircle}
            title="WhatsApp chat"
            body="Instant help. Quick replies."
            sub="Share images & get confirmation fast"
            href={`https://wa.me/${contact.whatsapp}`}
            cta="Chat now"
          />
        </aside>
      </section>
    </PageShell>
  );
}

function Field({ label, name, type = "text", required }: { label: string; name: string; type?: string; required?: boolean }) {
  const id = `contact-field-${name}`;
  return (
    <div>
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:border-accent focus:outline-none"
      />
    </div>
  );
}


function ContactCard({
  icon: Icon,
  title,
  body,
  sub,
  href,
  cta,
}: {
  icon: typeof Phone;
  title: string;
  body: string;
  sub: string;
  href: string;
  cta: string;
}) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-soft text-accent">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 font-display text-base font-semibold">{title}</h3>
      <p className="mt-1 text-sm font-medium">{body}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
      <a href={href} className="mt-4 inline-block text-sm font-semibold text-accent">
        {cta} →
      </a>
    </div>
  );
}
